import { getLogout } from '../redux/main/authSlice';
import { store } from '../redux/store';

/**
 * Servizio per gestire le chiamate API con gestione automatica degli errori 401
 * In caso di errore 401, l'utente viene automaticamente disconnesso e reindirizzato alla pagina di login
 */
export const apiService = {
  /**
   * Effettua una chiamata API con gestione automatica degli errori 401
   * @param {string} url - URL dell'API
   * @param {Object} options - Opzioni per la chiamata fetch
   * @returns {Promise<any>} - Promise con il risultato della chiamata
   */
  async fetch(url, options = {}) {
    try {
      console.log('Chiamata API a:', url);
      console.log('Opzioni:', JSON.stringify(options));
      
      const response = await fetch(url, options);
      
      console.log('Risposta status:', response.status);
      
      // Se la risposta è 401, disconnetti l'utente e reindirizza alla pagina di login
      if (response.status === 401) {
        console.error('Sessione scaduta o token non valido. Reindirizzamento alla pagina di login.');
        
        // Rimuovi i dati di sessione
        localStorage.removeItem('installamelo-auth-user');
        localStorage.removeItem('installamelo-auth-token');
        
        // Dispatch del logout
        store.dispatch(getLogout());
        
        // Forza il reindirizzamento alla pagina di login
        setTimeout(() => {
          console.log('Reindirizzamento forzato alla pagina di login...');
          window.location.replace('/login');
        }, 100);
        
        return Promise.reject(new Error('Sessione scaduta. Effettua nuovamente il login.'));
      }
      
      // Se la risposta non è ok, lancia un errore
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Errore nella risposta:', errorData);
        return Promise.reject(errorData);
      }
      
      // Se la risposta è ok, restituisci i dati
      const data = await response.json();
      console.log('Dati ricevuti:', data);
      return data;
    } catch (error) {
      console.error('Errore nella chiamata API:', error);
      
      // Verifica se l'errore è di tipo "network error" (impossibile contattare il server)
      if (error.message && (
          error.message.includes('Failed to fetch') || 
          error.message.includes('Network Error') || 
          error.message.includes('NetworkError') ||
          error.message.includes('network error') ||
          !navigator.onLine
      )) {
        console.error('Errore di connessione al server. Reindirizzamento alla pagina di login.');
        
        // Rimuovi i dati di sessione
        localStorage.removeItem('installamelo-auth-user');
        localStorage.removeItem('installamelo-auth-token');
        
        // Dispatch del logout
        store.dispatch(getLogout());
        
        // Forza il reindirizzamento alla pagina di login
        setTimeout(() => {
          console.log('Reindirizzamento forzato alla pagina di login...');
          window.location.replace('/login');
        }, 100);
        
        return Promise.reject(new Error('Impossibile contattare il server. Effettua nuovamente il login.'));
      }
      
      return Promise.reject(error);
    }
  },
  
  /**
   * Effettua una chiamata GET
   * @param {string} url - URL dell'API
   * @param {Object} options - Opzioni aggiuntive per la chiamata fetch
   * @returns {Promise<any>} - Promise con il risultato della chiamata
   */
  get(url, options = {}) {
    return this.fetch(url, {
      method: 'GET',
      ...options
    });
  },
  
  /**
   * Effettua una chiamata POST
   * @param {string} url - URL dell'API
   * @param {Object} data - Dati da inviare nel body
   * @param {Object} options - Opzioni aggiuntive per la chiamata fetch
   * @returns {Promise<any>} - Promise con il risultato della chiamata
   */
  post(url, data, options = {}) {
    return this.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
  },
  
  /**
   * Effettua una chiamata PATCH
   * @param {string} url - URL dell'API
   * @param {Object} data - Dati da inviare nel body
   * @param {Object} options - Opzioni aggiuntive per la chiamata fetch
   * @returns {Promise<any>} - Promise con il risultato della chiamata
   */
  patch(url, data, options = {}) {
    return this.fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
  },
  
  /**
   * Effettua una chiamata DELETE
   * @param {string} url - URL dell'API
   * @param {Object} options - Opzioni aggiuntive per la chiamata fetch
   * @returns {Promise<any>} - Promise con il risultato della chiamata
   */
  delete(url, options = {}) {
    return this.fetch(url, {
      method: 'DELETE',
      ...options
    });
  },

  /**
   * Effettua una chiamata POST inviando FormData (es. per upload file)
   * @param {string} url - URL dell'API
   * @param {FormData} formData - Dati FormData da inviare nel body
   * @param {Object} options - Opzioni aggiuntive per la chiamata fetch
   * @returns {Promise<any>} - Promise con il risultato della chiamata
   */
  postFormData(url, formData, options = {}) {
    // Nota: Non impostiamo 'Content-Type'. Il browser lo farà automaticamente
    // con il boundary corretto quando invia FormData.
    return this.fetch(url, {
      method: 'POST',
      body: formData,
      ...options // Permette di passare headers aggiuntivi se necessario (es. Authorization)
    });
  }
};
